import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { RefreshingIndicator } from "@shared/components/ListLoading";
import EmptyItems from "@shared/components/EmptyItems";
import { useMaintenanceWindow, useUpcomingWindow } from "@shared/hooks/useMaintenanceStatus";
import { buildPlatformMaintenanceRow } from "kontrolEdge/helpers/platformMaintenanceRow";
import { formatDuration } from "kontrolEdge/helpers/formatDuration";
import MonitoringUnavailable from "../MonitoringUnavailable";
import TenantOutOfScope from "../TenantOutOfScope";
import { MaintenanceDetailsModal, OutageDetailsModal } from "../OutageDetailsModal";
import { useManagerOverviewData } from "../hooks/useManagerOverviewData";
import { FailoverTableRow, MaintenanceTableRow } from "../Types";
import { AllocationPanel } from "./AllocationPanel";
import { AttentionPanel, SiteFilter } from "./AttentionPanel";
import { SiteSelection } from "./SitesDiagram";
import { MaintenancePanel } from "./MaintenancePanel";
import { ChipIssues, ServiceScopeChips } from "./ServiceScopeChips";
import { SitesPanel } from "./SitesPanel";
import { isExtenderPopEnabled } from "@shared/config/featureFlags";
import { useWindowSize } from "@shared/hooks/useWindowSize";
import {
  canReadSiteLatency,
  useSiteLatencyMatrix,
} from "kontrolEdge/components/SiteLatencyMatrix/useSiteLatencyMatrix";
import { buildSiteLatencyLookup } from "kontrolEdge/components/SiteLatencyMatrix/siteLatencyLookup";
import { MapCpeLatency, MapLatency, buildCpeLegLatencyLookup } from "./mapModel";
import { useCpeBackhaulLatency } from "../hooks/useCpeBackhaulLatency";
import {
  AttentionItem,
  MaintenanceFilter,
  ServiceScope,
  TenantScope,
  buildAttentionItems,
  isOpenIssue,
  serviceHealthBySite,
  isInTenantScope,
  kindInfo,
  scopeTitle,
  lastOutage,
  maintenanceFilterRange,
  summarizeAllocation,
  summarizeKinds,
  summarizeSites,
  visibleMaintenance,
} from "./overviewModel";

function toOutageRow(item: AttentionItem): FailoverTableRow | null {
  const { outage } = item;
  if (!outage) return null;
  return {
    date: outage.date,
    description: outage.description,
    customer: outage.customer?.name ?? "–",
    duration: formatDuration(outage.duration),
    site: outage.site ?? "–",
    summary: outage.summary ?? "",
    detail: outage.detail ?? "",
    incidentId: outage.incidentId ?? "",
  };
}

/**
 * The Kontrol Edge Overview, reorganised to answer three questions on the
 * first screen (project "El Overview dice qué atender y qué viene"):
 *
 *   1. Is everything fine?   — the scope chips and the figures under Sites.
 *   2. What do I look at?    — Attention required.
 *   3. What is coming?       — Maintenance.
 *
 * It reads the same four payloads as the classic page, through the same cached
 * hook, so switching between the two never refetches.
 */
export function OverviewActionCenter() {
  const api = useAxiosAuth0();
  const navigate = useNavigate();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const tenantType = useAppSelector((state) => state.userProfile.TenantType);
  const tenantScope: TenantScope = useMemo(
    () => ({ tenantId, isViewingAllCustomers: tenantId === partnerId }),
    [tenantId, partnerId]
  );

  const { data, loading, hasData, isServiceFetched, hasServiceError, isOutOfScope, isRefreshing } =
    useManagerOverviewData(api, tenantId);

  // Below `lg` the panels stack (CTX-1380). Stacked, a full map and every
  // service ran to 8,000 px on a phone, so the map starts folded and the list
  // opens on the services that need attention.
  const isStacked = !useWindowSize().isDesktop;

  // The DCI lines carry the RTT between their sites (CTX-843) for the callers
  // the backend serves the matrix to; anyone else is never asked for it, so
  // their map stays as it was. A failed request leaves the lines without RTT
  // instead of holding the map back.
  const canSeeLatency = canReadSiteLatency(tenantType);
  const { data: latencyMatrix } = useSiteLatencyMatrix(canSeeLatency);
  const latency: MapLatency | null = useMemo(
    () =>
      canSeeLatency && latencyMatrix
        ? {
            lookup: buildSiteLatencyLookup(latencyMatrix),
            measuredAt: latencyMatrix.measuredAt,
          }
        : null,
    [canSeeLatency, latencyMatrix]
  );
  // The tenant's own Backhaul legs (CTX-1461): every tenant type, scoped by the
  // backend. A failure only leaves the CPE lines without RTT.
  const { data: backhaulLatency } = useCpeBackhaulLatency(api, tenantId);
  const cpeLatency: MapCpeLatency | null = useMemo(
    () =>
      backhaulLatency
        ? { lookup: buildCpeLegLatencyLookup(backhaulLatency.legs), measuredAt: backhaulLatency.measuredAt }
        : null,
    [backhaulLatency]
  );

  const [scope, setScope] = useState<ServiceScope>("all");
  /** The pin picked on the map; the list narrows to its sites. */
  const [siteFilter, setSiteFilter] = useState<(SiteFilter & { key: string }) | null>(null);
  const handleSelectNode = useCallback(
    (node: SiteSelection | null) =>
      setSiteFilter(node ? { key: node.key, siteIds: node.siteIds, label: node.label } : null),
    []
  );
  const [maintenanceFilter, setMaintenanceFilter] = useState<MaintenanceFilter>({ preset: "current" });
  // Another tenant has other sites and kinds: a pin or chip picked for the
  // last one would leave the list filtered down to nothing.
  useEffect(() => {
    setScope("all");
    setSiteFilter(null);
    setMaintenanceFilter({ preset: "current" });
  }, [tenantId]);
  const [selectedOutage, setSelectedOutage] = useState<FailoverTableRow | null>(null);
  const [selectedMaintenance, setSelectedMaintenance] = useState<MaintenanceTableRow | null>(null);

  const platformRow = buildPlatformMaintenanceRow(
    useMaintenanceWindow("Kontrol Edge"),
    useUpcomingWindow("Kontrol Edge")
  );

  const kinds = useMemo(
    () => summarizeKinds(data.serviceStatus, tenantScope),
    [data.serviceStatus, tenantScope]
  );

  const ports = useMemo(
    () => (data.usage?.data ?? []).filter((port) => isInTenantScope(port, tenantScope)),
    [data.usage, tenantScope]
  );
  const sites = useMemo(() => summarizeSites(ports), [ports]);
  const allocation = useMemo(() => summarizeAllocation(ports), [ports]);

  // "Today" is read once per payload, not per render: the lists must not
  // reshuffle while the user is looking at them.
  const now = useMemo(() => new Date(), [data]);

  const placements = data.sites?.placements;
  const scopedSiteHealth = useMemo(
    () => serviceHealthBySite({ serviceStatus: data.serviceStatus, placements: placements ?? {}, scope, tenantScope }),
    [data.serviceStatus, placements, scope, tenantScope]
  );
  const cloudSiteHealth = useMemo(
    () =>
      serviceHealthBySite({
        serviceStatus: data.serviceStatus,
        placements: placements ?? {},
        scope: "cloudConnectivity",
        tenantScope,
      }),
    [data.serviceStatus, placements, tenantScope]
  );

  // The customer's CPEs, while the CPE view is enabled (hidden in production).
  const cpes = useMemo(() => (isExtenderPopEnabled() ? data.sites?.cpes ?? [] : []), [data.sites]);

  const siteNames = useMemo(
    () => Object.fromEntries((data.sites?.sites ?? []).map((site) => [site.datacenterId, site.displayName])),
    [data.sites]
  );

  // The list follows the chip; the open-issue count never does — "4 need
  // attention" is about the tenant, not about the chip someone clicked.
  const listItems = useMemo(
    () =>
      buildAttentionItems({
        serviceStatus: data.serviceStatus,
        failoverRecords: data.failover?.failoverRecords ?? [],
        scope,
        tenantScope,
        now,
        placements: data.sites?.placements,
        siteNames,
        labels: data.sites?.labels,
        cpes,
      }),
    [data.serviceStatus, data.failover, data.sites, siteNames, scope, tenantScope, now, cpes]
  );
  const openIssues: ChipIssues = useMemo(() => {
    const open = buildAttentionItems({
      serviceStatus: data.serviceStatus,
      failoverRecords: data.failover?.failoverRecords ?? [],
      scope: "all",
      tenantScope,
      now,
    }).filter(isOpenIssue);
    return { count: open.length, isCritical: open.some((item) => item.severity === "critical") };
  }, [data.serviceStatus, data.failover, tenantScope, now]);

  const maintenance = useMemo(
    () =>
      visibleMaintenance(
        data.maintenance?.maintenanceRecords ?? [],
        tenantScope,
        now,
        maintenanceFilterRange(maintenanceFilter, now)
      ),
    [data.maintenance, tenantScope, now, maintenanceFilter]
  );

  const handleOpenAttention = useCallback(
    (item: AttentionItem) => {
      if (item.path) {
        navigate(item.path);
        return;
      }
      setSelectedOutage(toOutageRow(item));
    },
    [navigate]
  );

  // "Nothing came back" has two opposite causes: the tenant really has no
  // services, or the request failed. Only the first is an empty state (CTX-547).
  if (isServiceFetched && !hasData) {
    if (isOutOfScope) return <TenantOutOfScope />;
    return hasServiceError ? <MonitoringUnavailable /> : <EmptyItems />;
  }

  return (
    // pb-20: the last panel's last row stays clear of the floating "+" button.
    <div className="relative space-y-4 px-1 pb-20 pt-2 sm:px-3 sm:pt-4">
      <RefreshingIndicator active={isRefreshing} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="flex flex-col lg:col-span-8">
          <SitesPanel
            sites={sites}
            locations={data.sites}
            scope={scope}
            serviceHealthBySite={scopedSiteHealth}
            cloudHealthBySite={cloudSiteHealth}
            selectedNodeKey={siteFilter?.key ?? null}
            onSelectNode={handleSelectNode}
            toolbar={
              <ServiceScopeChips
                kinds={kinds}
                cpeCount={cpes.length}
                openIssues={openIssues}
                scope={scope}
                onScopeChange={setScope}
              />
            }
            isLoading={loading.usage || loading.service}
            foldable={isStacked}
            latency={latency}
            cpeLatency={cpeLatency}
          />
        </div>
        {/* On desktop the list takes the map's height and scrolls, instead of
            stretching the row; stacked on mobile it keeps a usable minimum. */}
        {/* The list keeps a usable height even when the panel beside it is short
            (a tenant whose sites have no location shows tiles, not a map). */}
        <div className="relative flex min-h-[26rem] flex-col lg:col-span-4">
          <AttentionPanel
            className="lg:absolute lg:inset-0"
            siteFilter={siteFilter}
            onClearSiteFilter={() => setSiteFilter(null)}
            title={scopeTitle(scope)}
            items={listItems}
            lastOutage={lastOutage(data.failover?.failoverRecords ?? [], tenantScope)}
            isLoading={loading.service || loading.failover}
            onOpen={handleOpenAttention}
            onViewHistory={() => navigate("/kontrol/outages", { state: { selectedView: "outages" } })}
            compact={isStacked}
          />
        </div>
        <div className="flex flex-col lg:col-span-8">
          <AllocationPanel
            summary={allocation}
            highlight={scope === "all" || scope === "cpe" ? null : kindInfo(scope).allocationKey}
            isLoading={loading.usage}
          />
        </div>
        <div className="flex flex-col lg:col-span-4">
          <MaintenancePanel
            entries={maintenance}
            platformRow={platformRow}
            filter={maintenanceFilter}
            onFilterChange={setMaintenanceFilter}
            isLoading={loading.maintenance}
            onViewAll={() => navigate("/kontrol/outages", { state: { selectedView: "maintenance" } })}
            onOpen={setSelectedMaintenance}
          />
        </div>
      </div>

      <OutageDetailsModal outage={selectedOutage} onClose={() => setSelectedOutage(null)} />
      <MaintenanceDetailsModal maintenance={selectedMaintenance} onClose={() => setSelectedMaintenance(null)} />
    </div>
  );
}
